import { useState } from "react";
import type { AmzFieldDef, AmzProductDetail } from "../../../types/amazon";
import {
  attrEqual,
  bulletKey,
  codePointLength,
  getFieldValue,
  ngHits,
  setFieldValue,
  summarizeValue,
} from "../../../lib/amazonContent";
import { AttrField } from "../AttrField";
import { Badge } from "../AmzUi";
import type { ProductEditor } from "../useProductEditor";

interface PageTabProps {
  detail: AmzProductDetail;
  editor: ProductEditor;
  aiOpen: boolean;
  aiTarget: string | null;
  onAi: (fieldKey: string) => void;
  onReview: () => void;
  onDiscard: () => void;
}

const SPECIAL_KEYS = ["item_name", "title_differentiation", "bullet_point", "product_description"];

function fallbackDef(key: string, label: string, type: AmzFieldDef["type"], multi = false): AmzFieldDef {
  return {
    key,
    label,
    group: "product_details",
    groupTitle: "商品詳細",
    type,
    required: false,
    editable: true,
    aiAllowed: true,
    hidden: false,
    multi,
    hasLanguageTag: true,
  };
}

function NgBoxes({ text }: { text: string }) {
  return (
    <>
      {ngHits(text).map((h) => (
        <div key={h.word} className="amz-ng">
          表現チェック：「{h.word}」— {h.law}上の注意表現です（自動削除はしません）
        </div>
      ))}
    </>
  );
}

interface TextCardProps {
  title: string;
  def: AmzFieldDef;
  fieldKey: string;
  rows: number;
  descStyle?: boolean;
  detail: AmzProductDetail;
  editor: ProductEditor;
  target: boolean;
  onAi: (fieldKey: string) => void;
}

function TextCard({ title, def, fieldKey, rows, descStyle, detail, editor, target, onAi }: TextCardProps) {
  const value = (getFieldValue(editor.draft, fieldKey, def) as string) ?? "";
  const curValue = detail.current ? summarizeValue(detail.current, fieldKey, def) : "";
  const changed = detail.current !== null && !attrEqual(detail.current[fieldKey], editor.draft[fieldKey]);
  const n = codePointLength(value);
  const max = def.softMax ?? def.maxLength ?? 0;
  const hardOver = !!def.maxLength && n > def.maxLength;
  return (
    <section className={`amz-field ${target ? "target" : ""}`}>
      <div className="amz-field-head">
        <div className="amz-field-title">
          <h3>{title}</h3>
          {def.required && <span className="amz-req">*</span>}
          {changed && (
            <Badge tone="accent" size="sm">
              下書き変更あり
            </Badge>
          )}
        </div>
        <div className="amz-field-tools">
          <span className={`amz-count-text ${max > 0 && n > max ? "over" : ""}`}>
            {n}
            {max > 0 ? ` / ${max}` : ""}
          </span>
          <button type="button" className="amz-ai-pill" onClick={() => onAi(fieldKey)}>
            AI
          </button>
        </div>
      </div>
      <textarea
        className={`amz-textarea ${descStyle ? "desc" : ""}`}
        rows={rows}
        value={value}
        onChange={(e) =>
          editor.edit((d) => setFieldValue(d, fieldKey, e.target.value, def, detail.marketplaceId), [fieldKey])
        }
      />
      {hardOver && <div className="amz-ng">Amazonの文字数上限（{def.maxLength}文字）を超えています。送信前に短くしてください。</div>}
      {changed && (
        <div className="amz-cur-box">
          <span>
            <b>Amazon現在値</b>&emsp;{curValue || "（未設定）"}
          </span>
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              editor.revertField(fieldKey);
            }}
          >
            元に戻す
          </a>
        </div>
      )}
      <NgBoxes text={value} />
    </section>
  );
}

function BulletList({
  detail,
  editor,
  aiOpen,
  aiTarget,
  onAi,
}: {
  detail: AmzProductDetail;
  editor: ProductEditor;
  aiOpen: boolean;
  aiTarget: string | null;
  onAi: (fieldKey: string) => void;
}) {
  const def = detail.fields.fields.find((f) => f.key === "bullet_point") ?? fallbackDef("bullet_point", "商品の仕様", "textarea", true);
  const maxItems = def.maxItems ?? 10;
  const soft = def.softMax ?? 100;
  const rows = (getFieldValue(editor.draft, "bullet_point", def) as string[]) ?? [];
  const curRows = detail.current ? ((getFieldValue(detail.current, "bullet_point", def) as string[]) ?? []) : null;
  const [extra, setExtra] = useState(0);
  const shown = Math.min(maxItems, Math.max(5, rows.length, extra));
  const [drag, setDrag] = useState<number | null>(null);
  const [over, setOver] = useState<number | null>(null);

  const value = (i: number) => rows[i] ?? "";
  const move = (from: number, to: number) => {
    const arr = Array.from({ length: shown }, (_, i) => value(i));
    const [m] = arr.splice(from, 1);
    arr.splice(to, 0, m);
    const touched = arr.map((v, i) => (v !== value(i) ? bulletKey(i) : null)).filter((k): k is string => k !== null);
    editor.edit((d) => setFieldValue(d, "bullet_point", arr, def, detail.marketplaceId), touched);
  };

  return (
    <section className="amz-field">
      <div className="amz-field-head">
        <div className="amz-field-title">
          <h3>Bullet Points</h3>
          <span className="amz-req">*</span>
        </div>
        <span className="amz-hint">ドラッグで並び替え · 推奨{soft}文字まで</span>
      </div>
      {Array.from({ length: shown }, (_, i) => {
        const v = value(i);
        const n = codePointLength(v);
        const key = bulletKey(i);
        const changed = curRows !== null && (curRows[i] ?? "") !== v;
        const isTarget = aiOpen && aiTarget === key;
        return (
          <div
            key={i}
            className={`amz-bullet ${isTarget ? "target" : ""} ${drag === i ? "dragging" : ""} ${over === i && drag !== null && drag !== i ? "dragover" : ""}`}
            onDragOver={(e) => {
              e.preventDefault();
              if (over !== i) setOver(i);
            }}
            onDrop={(e) => {
              e.preventDefault();
              const from = drag;
              setDrag(null);
              setOver(null);
              if (from !== null && from !== i) move(from, i);
            }}
          >
            <div
              className="amz-handle"
              draggable
              title="ドラッグで並び替え"
              onDragStart={(e) => {
                e.dataTransfer.effectAllowed = "move";
                try {
                  e.dataTransfer.setData("text/plain", String(i));
                } catch {
                  /* 一部ブラウザは setData が失敗する */
                }
                setDrag(i);
              }}
              onDragEnd={() => {
                setDrag(null);
                setOver(null);
              }}
            >
              ⋮⋮
            </div>
            <div className="amz-bullet-body">
              <div className="amz-field-head">
                <div className="amz-field-title">
                  <span className="amz-bullet-label">Bullet {i + 1}</span>
                  {changed && (
                    <Badge tone="accent" size="sm">
                      下書き変更あり
                    </Badge>
                  )}
                </div>
                <div className="amz-field-tools">
                  <span className={`amz-count-text ${n > soft ? "over" : ""}`}>
                    {n} / {soft}
                  </span>
                  <button type="button" className="amz-ai-pill" onClick={() => onAi(key)}>
                    AI
                  </button>
                </div>
              </div>
              <textarea
                className="amz-textarea"
                rows={2}
                value={v}
                onChange={(e) => editor.edit((d) => setFieldValue(d, key, e.target.value, def, detail.marketplaceId), [key])}
              />
              {changed && (
                <div className="amz-cur-box">
                  <span>
                    <b>Amazon現在値</b>&emsp;{curRows?.[i] || "（未設定）"}
                  </span>
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      editor.revertField(key);
                    }}
                  >
                    元に戻す
                  </a>
                </div>
              )}
              <NgBoxes text={v} />
            </div>
          </div>
        );
      })}
      {shown < maxItems && (
        <div>
          <button type="button" className="amz-btn sm" onClick={() => setExtra(shown + 1)}>
            ＋ 箇条書きを追加（{shown}/{maxItems}）
          </button>
        </div>
      )}
    </section>
  );
}

export function PageTab({ detail, editor, aiOpen, aiTarget, onAi, onReview, onDiscard }: PageTabProps) {
  const fields = detail.fields.fields;
  const find = (k: string) => fields.find((f) => f.key === k);
  const nameDef = find("item_name") ?? fallbackDef("item_name", "商品名", "text");
  // Amazon.co.jp 2026-10: 商品名は75文字まで。素材・用途・機能などの詳細は「商品のハイライト」（125文字）へ
  const hlDef = find("title_differentiation") ?? { ...fallbackDef("title_differentiation", "商品のハイライト", "text"), maxLength: 125, softMax: 125 };
  const descDef = find("product_description") ?? fallbackDef("product_description", "商品説明文", "textarea");
  const isTarget = (k: string) => aiOpen && aiTarget === k;

  const groups = detail.fields.groups
    .filter((g) => g.key !== "images")
    .map((g) => ({
      ...g,
      fields: fields.filter((f) => f.group === g.key && !f.hidden && !SPECIAL_KEYS.includes(f.key)),
    }))
    .filter((g) => g.fields.length > 0);

  return (
    <>
      {!detail.registered && (
        <div className="amz-warn-box">
          この商品はAmazon未登録です。下書きを「変更を確認」から新規登録（PUT）できます。
        </div>
      )}
      <div className="amz-note-bar">
        <span>編集内容はローカル下書きに保存されます。Amazonへは「変更を確認」から反映します。</span>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {detail.registered && editor.changes.length > 0 && (
            <button type="button" className="amz-btn" onClick={onDiscard}>
              下書きを破棄
            </button>
          )}
          <button type="button" className="amz-btn outline" onClick={onReview}>
            AIで商品ページをレビュー
          </button>
        </div>
      </div>

      <TextCard
        title="商品タイトル"
        def={nameDef}
        fieldKey="item_name"
        rows={2}
        detail={detail}
        editor={editor}
        target={isTarget("item_name")}
        onAi={onAi}
      />
      <TextCard
        title="商品のハイライト"
        def={hlDef}
        fieldKey="title_differentiation"
        rows={2}
        detail={detail}
        editor={editor}
        target={isTarget("title_differentiation")}
        onAi={onAi}
      />
      <BulletList detail={detail} editor={editor} aiOpen={aiOpen} aiTarget={aiTarget} onAi={onAi} />
      <TextCard
        title="商品説明"
        def={descDef}
        fieldKey="product_description"
        rows={5}
        descStyle
        detail={detail}
        editor={editor}
        target={isTarget("product_description")}
        onAi={onAi}
      />

      <section className="amz-field">
        <div className="amz-attrs-head">
          <h3 style={{ fontSize: 14, fontWeight: 600 }}>その他の商品属性</h3>
          <span className="amz-hint">
            商品タイプ <span className="amz-mono">{detail.productType}</span> の定義から生成 · 在庫・価格関連の属性は表示していません
          </span>
        </div>
        <div className="amz-attrs">
          {groups.map((g) => (
            <div key={g.key} className="amz-attr-group">
              <span className="amz-attr-group-title">{g.title}</span>
              <div className="amz-attr-grid">
                {g.fields.map((f) => (
                  <AttrField
                    key={f.key}
                    def={f}
                    draft={editor.draft}
                    current={detail.current}
                    isTarget={isTarget(f.key)}
                    onChange={(v) => editor.edit((d) => setFieldValue(d, f.key, v, f, detail.marketplaceId), [f.key])}
                    onAi={() => onAi(f.key)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}
